// React imports
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";

// MUI imports
import {Alert, Box, Card, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar} from "@mui/material";

// MUI Icons imports
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Customized MUI components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Steps imports
import SelectTripStep from "./SelectTripStep";
import TravelerDetailsStep from "./TravelerDetailsStep";
import TripCustomizationStep from "./TripCustomizationStep";
import ReviewPaymentStep, { buildReviewPricing } from "./ReviewPaymentStep";
import OrderSummaryPanel from "./OrderSummaryPanel";
import SendPaymentLinkModal from "components/SendPaymentLinkModal";

// Other imports-
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { useAuth } from "auth/Auth";
import {
    WIZARD_STEPS, createEmptyTraveler, getTripNights, getOfferWindow,
    calculateAddonAmount, calculateAccommodationAmount,
} from "./hardcodedData";
import { previewPrivateObject } from "../../../utils/S3";
import { CANCELLATION_CATEGORY } from "utils/paymentTermConstants";
import { parseApiDate, toApiDate } from "utils/DateTimeConversion";
import { getServiceMaxCapacity } from "./TravelOrderUtility";
import { useScrollContainer } from "shell-v2/ScrollContainer";
import { formatAmount } from "./TravelOrderUtility";
import {
    EMPTY_OFFERINGS,
    deriveOfferings,
    getAllocatedComponentIds,
    inferAddonCategory,
} from "./deriveOfferings";

const GATEWAY_PAYMENT_METHOD = {
  stripe: "Card",
  razorpay: "UPI",
};

// Stepper Component

const OrderWizardStepper = ({
  steps,
  activeStep,
  completedSteps,
  onStepClick,
  allStepsClickable = false,
}) => (
    <Box sx={{ width: "100%", overflowX: "auto", mt: 2.5, pt: 0.75, pb: 0.25 }}>
      <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            minWidth: { xs: 640, md: "100%" },
            px: 0.5,
          }}
      >
        {steps.map((label, index) => {
          const isCompleted = completedSteps.has(index) && index < activeStep;
          const isActive = activeStep === index;
          const isPast = index < activeStep || isCompleted;
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;
          const lineBeforeBright = !isFirst && index <= activeStep;
          const lineAfterBright = !isLast && isPast;
          const isClickable = allStepsClickable || isCompleted || index <= activeStep;

          return (
              <Box
                  key={label}
                  onClick={() => isClickable && onStepClick?.(index)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 56,
                    cursor: isClickable ? "pointer" : "default",
                    opacity: isClickable ? 1 : 0.6,
                  }}
              >
                <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        bgcolor: lineBeforeBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                        visibility: isFirst ? "hidden" : "visible",
                        mr: isFirst ? 0 : -0.25,
                      }}
                  />
                  <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        flexShrink: 0,
                        zIndex: 2,
                        transition: "all 0.2s ease",
                        boxSizing: "border-box",
                        ...(isCompleted
                            ? { bgcolor: "#1F8A56", color: "#fff", border: "2px solid #1F8A56" }
                            : isActive
                                ? { bgcolor: "#fff", color: "#1565C8", border: "2px solid #fff" }
                                : { bgcolor: "#0E2348", color: "#fff", border: "2px solid rgba(255,255,255,0.65)" }),
                      }}
                  >
                    {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
                  </Box>
                  <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        bgcolor: lineAfterBright ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.28)",
                        visibility: isLast ? "hidden" : "visible",
                        ml: isLast ? 0 : -0.25,
                      }}
                  />
                </Box>
                <MDTypography
                    variant="caption"
                    sx={{
                      mt: 1,
                      textAlign: "center",
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      letterSpacing: "0.03em",
                      color: isActive || isCompleted ? "#fff" : "rgba(255,255,255,0.72)",
                      lineHeight: 1.25,
                      maxWidth: 90,
                    }}
                >
                  {label}
                </MDTypography>
              </Box>
          );
        })}
      </Box>
    </Box>
);

const mapPackageFromApi = (tripPackageSummary, detail) => {

    const durationDays = tripPackageSummary.duration_days;
    const nights = durationDays ? Math.max(durationDays - 1, 0) : null;
    const services = detail?.components || [];
    const addons = Array.isArray(detail?.addons) ? detail.addons : [];
    const itinerary = detail?.itineraries || [];
    const itineraryDays = (itinerary[0]?.itinerary_days || [])
        .slice()
        .sort((a, b) => (a.day_number ?? 0) - (b.day_number ?? 0));

    const linkedTrips = tripPackageSummary.trips.map((trip) => ({
        id: trip.id, title: trip.title, primary_country: trip.primary_country, status: trip.status,
    }));

    return {
        id: tripPackageSummary.id,
        title: tripPackageSummary.title,
        status: tripPackageSummary.status,
        sequence: tripPackageSummary.sequence,
        duration_days: tripPackageSummary.duration_days,
        duration: durationDays ? `${durationDays} Days / ${nights} Nights` : "-",
        base_price: tripPackageSummary.base_price,
        currency: tripPackageSummary.currency,
        created_at: tripPackageSummary.created_at,
        updated_at: tripPackageSummary.updated_at,
        trips: linkedTrips,
        destinations: linkedTrips.map((trip) => trip.primary_country),
        image: tripPackageSummary.banner_image || "",
        // IF detail endpoint brought complete package info.
        description: detail ? detail.description : "",
        cities: detail ? detail.cities : "",
        tags: detail ? detail.tags : "",
        package_tier: detail ? detail.package_tier : "",
        payment_templates: detail ? detail.payment_templates : [],
        components: detail ? detail.components : [],
        departure_dates: (detail?.departure_dates || [])
            .slice()
            .sort((a, b) => new Date(a.departure_date) - new Date(b.departure_date)),
        inclusions: itinerary ? itinerary[0]?.inclusion || "" : "",
        // Evaluated
        services: services,
        itinerary_days: itineraryDays,
        merchant_allocations: detail?.merchant_allocations || [],
        payment_template_ids: Array.isArray(detail?.payment_templates) ? detail?.payment_templates : [],
        addons: addons,
        itineraries: itinerary ? itinerary[0] : "",
        faqs: detail?.faqs?.faqs || detail?.faqs || [],
    }
};


function CreateTravelOrder() {
  // The window in classic; <main> in the v2 frame (see shell-v2/ScrollContainer).
  const scroller = useScrollContainer();

  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { id: editOrderId } = useParams();
  const isEditMode = Boolean(editOrderId);

  const [taxes, setTaxes] = useState([]);
  const [originalOrderStatus, setOriginalOrderStatus] = useState(null);
  const [originalOrderSubStatus, setOriginalOrderSubStatus] = useState(null);

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepError, setStepError] = useState("");
  const [orderRecordId, setOrderRecordId] = useState(editOrderId || null);
  const [isSavingStep, setIsSavingStep] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // The screen must be scrolled to top, whenever user hits the next or back step button.
  useEffect(() => {
    scroller.scrollTo({ top: 0, behavior: "smooth" });
    // Only on step change: `scroller` is a fresh object every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Error handler
  const [fieldErrors, setFieldErrors] = useState({});
  const setFieldError = (fieldKey, message) => {
    setFieldErrors((prev) => {
      if (prev[fieldKey] === message) return prev; // no actual change, skip re-render
      return { ...prev, [fieldKey]: message };
    });
  };

  // Wizard Step - 1 : Select Trip [ State management ]
  const [isLoadingTripPackages, setIsLoadingTripPackages] = useState(false);
  const [tripPackages, setTripPackages] = useState([]);
  const [selectedTripPackage, setSelectedTripPackage] = useState(null);

  const [searchDeparture, setSearchDeparture] = useState("");
  const [travelAfterDate, setTravelAfterDate] = useState(new Date());
  const [travelBeforeDate, setTravelBeforeDate] = useState(null);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [isLoadingAllocations, setIsLoadingAllocations] = useState(false);
  const [allocationMap, setAllocationMap] = useState({});
  const [hssTemplateMap, setHssTemplateMap] = useState({});
  const [isLoadingHssTemplates, setIsLoadingHssTemplates] = useState(false);
  const [editOrderData, setEditOrderData] = useState(null);
  const [isStructureLocked, setIsStructureLocked] = useState(false);
  const isSupplementMode = Boolean(isEditMode && isStructureLocked);
  const isSelectionLocked = Boolean(isEditMode && isStructureLocked);

  const isOrderCancelledOrFailed = useMemo(() => {
    if (!isEditMode) return false;
    const s = String(originalOrderStatus || editOrderData?.status || "").trim().toLowerCase();
    const sub = String(originalOrderSubStatus || editOrderData?.sub_status || "").trim().toLowerCase();
    const fullyLockedStatuses = [
      "cancelled",
      "cancellation",
      "failed",
      "travel in progress",
      "travel completed",
    ];
    const fullyLockedSubStatuses = [
      "travel in progress",
      "travel completed",
      "cancelled by customer",
      "cancelled by organization",
      "cancelled - seats unavailable (refunded)",
      "failed",
      "payment failed",
    ];
    return fullyLockedStatuses.includes(s) || fullyLockedSubStatuses.includes(sub);
  }, [isEditMode, originalOrderStatus, originalOrderSubStatus, editOrderData?.status, editOrderData?.sub_status]);

  const isBothDraft = useMemo(() => {
    if (!isEditMode) return true;
    const s = String(originalOrderStatus || editOrderData?.status || "Draft").trim().toLowerCase();
    const sub = String(originalOrderSubStatus || editOrderData?.sub_status || "Draft").trim().toLowerCase();
    return s === "draft" && (sub === "draft" || sub === "");
  }, [isEditMode, originalOrderStatus, originalOrderSubStatus, editOrderData?.status, editOrderData?.sub_status]);

  const isAccountContactLocked = Boolean(isEditMode && (!isBothDraft || isStructureLocked));

  // Also used in step 2 and 3
  const [travelers, setTravelers] = useState(() => [createEmptyTraveler(true, true)]);

  // Wizard Step - 2 : Traveler Details [ State management ]
  const travelerStepRef = useRef(null);
  const [seatsError, setSeatsError] = useState("");
  const [secureAllocation, setSecureAllocation] = useState(0);
  const [isLoadingSeats, setIsLoadingSeats] = useState(false);

  // Wizard Step - 3 : Trip Customization [ State management ]
  const [selectedAccommodationIds, setSelectedAccommodationIds] = useState([]);
  const [accommodationTravelerMap, setAccommodationTravelerMap] = useState({});
  const [invalidAccommodationIds, setInvalidAccommodationIds] = useState([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [addonTravelerMap, setAddonTravelerMap] = useState({}); // { [addonId]: [travelerId, ...] }
  const [invalidAddonIds, setInvalidAddonIds] = useState([]);
  const [bookedSeatsMap, setBookedSeatsMap] = useState(null); // null = not loaded yet
  const [lockedAccommodationIds, setLockedAccommodationIds] = useState([]); // invoiced
  const [lockedAddonIds, setLockedAddonIds] = useState([]);                 // invoiced
  const [lockedAccommodationTravelerMap, setLockedAccommodationTravelerMap] = useState({});
  const [lockedAddonTravelerMap, setLockedAddonTravelerMap] = useState({});
  const [supplementAddonIds, setSupplementAddonIds] = useState([]);         // invoiced via a supplement, NOT in the base invoice
  const supplementInvoiceRef = useRef(null);

  // Wizard Step - 4 : Payment & Review [ State management ]
  const [paymentError, setPaymentError] = useState("");
  const [isEditingFromReview, setIsEditingFromReview] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [paymentTemplates, setPaymentTemplates] = useState([]);
  const [invalidPaymentTemplate, setInvalidPaymentTemplate] = useState(false);
  const [selectedPaymentTemplateId, setSelectedPaymentTemplateId] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState("");
  const [gatewayAvailability, setGatewayAvailability] = useState(null);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [gatewayError, setGatewayError] = useState("");
  const [paymentTermPolicies, setPaymentTermPolicies] = useState([]);
  const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [paymentLinkInvoiceId, setPaymentLinkInvoiceId] = useState(null);
  const [paymentLinkDefaultEmail, setPaymentLinkDefaultEmail] = useState("");
  const [bookingStatus, setBookingStatus] = useState("Draft");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [latestOrderId, setLatestOrderId] = useState("");
  const [createdOrderNumericId, setCreatedOrderNumericId] = useState(null);
  const [successToast, setSuccessToast] = useState("");
  // CHECK THIS : Need to work on this ?
  const [flightPreference, setFlightPreference] = useState("Economy");

  // -------------------------------------------------
  // Order Summary
  // -------------------------------------------------

  const selectedHssTemplate = useMemo(() => {
    const hssId = selectedDepartureDate?.hss_template_id ?? selectedDepartureDate?.hss_template;
    return hssId ? hssTemplateMap[hssId] : null;
  }, [selectedDepartureDate, hssTemplateMap]);

    const departureOfferings = useMemo(() => {
        if (!selectedTripPackage || !selectedDepartureDate) return EMPTY_OFFERINGS;
        const allocatedIds = getAllocatedComponentIds(
            selectedTripPackage.merchant_allocations,
            selectedDepartureDate.id,
            selectedTripPackage.id
        );
        return deriveOfferings({
            services: selectedTripPackage.services,
            addons: selectedTripPackage.addons,
            itineraryDays: selectedTripPackage.itinerary_days,
            allocatedIds,
        });
    }, [selectedTripPackage, selectedDepartureDate]);

    const isCustomizationStep = activeStep === 2;

    useEffect(() => {
        if (!selectedDepartureDate?.id) {
            setBookedSeatsMap(null);
            return;
        }
        let cancelled = false;
        const loadBookedSeats = async () => {
            try {
                const res = await axiosInstance.get("/travel-orders/addon-seats-booked", {
                    params: {
                        departure_date_id: selectedDepartureDate.id,
                        exclude_order_id: orderRecordId || undefined,
                    },
                });
                if (!cancelled) setBookedSeatsMap(res.data?.booked || {});
            } catch {
                if (!cancelled) setBookedSeatsMap(null); // backend still enforces on save
            }
        };
        loadBookedSeats();
        return () => { cancelled = true; };
    }, [selectedDepartureDate?.id, orderRecordId, isCustomizationStep]);

    // { [addonId]: seatsLeft } ; no entry = unlimited or unknown
    const addonSeatsLeft = useMemo(() => {
        const result = {};
        if (!bookedSeatsMap) return result;
        departureOfferings.other_addons.forEach(({ addon, linkedService }) => {
            if (!linkedService) return;
            const capacity = getServiceMaxCapacity(linkedService);
            if (capacity == null) return;
            result[addon.id] = Math.max(capacity - (bookedSeatsMap[linkedService.id] || 0), 0);
        });
        return result;
    }, [departureOfferings, bookedSeatsMap]);

    // Hide sold-out add-ons, but keep one that is already selected so it can be deselected.
    const visibleOtherAddons = useMemo(
        () => departureOfferings.other_addons.filter(
            (item) => addonSeatsLeft[item.addon.id] !== 0
                || selectedAddonIds.includes(item.addon.id)
                || lockedAddonIds.includes(item.addon.id)
        ),
        [departureOfferings, addonSeatsLeft, selectedAddonIds, lockedAddonIds]
    );

  // -------------------------------------------------
  // Step 1 : Select Trip
  // -------------------------------------------------

  // Retrieving Trip Packages.
  // DO SOMETHING TO MAKE SURE BACKEND CHECKS EVERYTHING TO MAKE SURE ONLY BOOKABLE PACKAGE ARE RETURNED.

  useEffect(() => {
      const loadPackages = async () => {
          setIsLoadingTripPackages(true);
          try {
              let includePackageId;
              if (isEditMode) {
                  const orderRes = await axiosInstance.get(`/travel-orders/${editOrderId}/edit`);
                  setEditOrderData(orderRes.data);
                  includePackageId = orderRes.data.trip_package_id;
              }
              const res = await axiosInstance.get('/trip-packages', {
                  params: { status: 'active', bookable_only: true, include_package_id: includePackageId },
              });
              const packages = Array.isArray(res.data) ? res.data : [];
              if (!packages.length) {
                  if (isEditMode) { setStepError("No bookable packages found for this order."); setIsLoadingOrderForEdit(false); }
                  return;
              }

              const detailed = await Promise.all(
                  packages.map(async (pkg) => {
                      try {
                          const [detailRes, heroImage] = await Promise.all([
                              axiosInstance.get(`/trip-packages/${pkg.id}`),
                              resolvePackageHeroImage(pkg.id),
                          ]);
                          const mapped = mapPackageFromApi(pkg, detailRes.data);
                          return { ...mapped, image: heroImage || mapped.image}
                      } catch {
                          return mapPackageFromApi(pkg, null);
                      }
                  })
              );
              setTripPackages(detailed);
          } catch (err) {
              console.log("Unable to retrieve packages :", err);
              if (isEditMode) { setStepError("Failed to load this order. Please try again."); setIsLoadingOrderForEdit(false); }
          } finally {
              setIsLoadingTripPackages(false);
          }
      };
    loadPackages();
  }, []);

  // Filtered Trip Packages

  const filteredTripPackages = useMemo(() => {
    const q = searchDeparture.trim().toLowerCase();
    const afterTime = travelAfterDate ? new Date(travelAfterDate).getTime() : null;
    const beforeTime = travelBeforeDate ? new Date(travelBeforeDate).getTime() : null;

    return tripPackages.filter((pkg) => {
      // --- text match (name / departure city) ---
      const matchesText =
          !q ||
          pkg.title.toLowerCase().includes(q) ||
          (pkg.trips || []).some((trip) => (trip.primary_country || "").toLowerCase().includes(q)) ||
          (pkg.cities || []).some((city) => city.toLowerCase().includes(q));

      if (!matchesText) return false;

      // --- date range match against departure_dates[].departure_date ---
      if (isSelectionLocked || (!afterTime && !beforeTime)) return true;

      const departure_dates = pkg.departure_dates || [];
      if (departure_dates.length === 0) return false; // no departures to check against

      return departure_dates.some((opt) => {
        if (!opt.departure_date) return false;
        const depTime = new Date(opt.departure_date).getTime();
        if (Number.isNaN(depTime)) return false;

        if (afterTime && depTime < afterTime) return false;
        if (beforeTime && depTime > beforeTime) return false;
        return true;
      });
    });
    }, [searchDeparture, tripPackages, travelAfterDate, travelBeforeDate, isSelectionLocked]);

  // When a trip package is selected.

  const handleSelectTripPackage = async (tripPackage) => {
    if (isSelectionLocked || isOrderCancelledOrFailed) return;
    // Clicking the already-selected card deselects it
    if (selectedTripPackage?.id === tripPackage?.id) {
        ClearPackageSelection();
        return;
    }

    if (isEditingFromReview && selectedTripPackage?.id !== tripPackage?.id) {
      setIsEditingFromReview(false);
    }
    setSelectedTripPackage(tripPackage);
    setSelectedDepartureDate(null);
    setFieldError("trip", null);
    setFieldError("departureDate", null);
    resetTripCustomization();
  };

  // Clear Package Selection.

  const ClearPackageSelection = () => {
    if (isSelectionLocked || isOrderCancelledOrFailed) return;
    setSelectedTripPackage(null);
    setSelectedDepartureDate(null);
    resetTripCustomization();
  };

  // Retrieve secured seats available for all the departures date belongs to selected trip package.

  useEffect(() => {
    const departureIds = (selectedTripPackage?.departure_dates || [])
        .map((opt) => opt.id)
        .filter(Boolean);

    if (!departureIds.length) {
        setAllocationMap({});
        return;
    }

    let cancelled = false;
    const loadAllocations = async () => {
        setIsLoadingAllocations(true);
        try {
                const res = await axiosInstance.get("/travel-orders/secured-allocation-batch", {
                    params: { departure_date_ids: departureIds.join(","), exclude_order_id: orderRecordId || undefined },
                });
                if (!cancelled) {
                    setAllocationMap(res.data?.allocations || {});
                }
            } catch (err) {
                if (!cancelled) {
                    setAllocationMap({});
                }
            } finally {
                if (!cancelled) setIsLoadingAllocations(false);
            }
        };

        loadAllocations();
        return () => {
            cancelled = true;
        };
    }, [selectedTripPackage?.id]);

  // Handle Departure Date Selection

  const handleChangeDepartureDate = (option) => {
        if (isSelectionLocked || isOrderCancelledOrFailed) return;
        if (!option) {
            setSelectedDepartureDate(null);
            setFieldError("departureDate", null);
            return;
        }

        // If allocations for this trip are still loading, we don't have a trustworthy
        // seat count yet - refuse to commit the change rather than risk overselling.
        if (isLoadingAllocations) {
            setFieldError("departureDate", "Checking seat availability for this departure date, please try again in a moment.")
            return;
        }

        const requiredSeats = travelers.length;
        const secured = allocationMap[option.id] ?? 0;
        const totalAllocation = option.total_allocation ?? 0;
        const seatsAvailable = Math.max(totalAllocation - secured, 0);

        if (requiredSeats > seatsAvailable) {
            setFieldError(
                "departureDate",
                `You have configured ${requiredSeats} traveler${requiredSeats === 1 ? "" : "s"}, but only ` +
                `${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"} ${seatsAvailable === 1 ? "is" : "are"} available ` +
                `on this departure date. Please select another departure date or remove a traveler.`
            );
            return;
        } else {
            setFieldError("departureDate", null);
        }

        if (selectedDepartureDate?.id !== option.id) {
            resetTripCustomization(); // each departure date owns its own addon catalog
        }

        setSelectedDepartureDate(option);
        setFieldError("departureDate", false);
    };

  // Retrieve package hero image url.

  const resolvePackageHeroImage = async (packageId) => {
    try {
        const response = await axiosInstance.get('/attachments/', {
            params: {
                parent_table: 'trip_packages',
                parent_id: packageId
            }
        });
        return await handlePreviewAttachment(response.data[0]);
    } catch (err) {
        console.error(`Error fetching attachments for package ${packageId}:`, err);
        return "";
    }
  };

  // Extracts package hero image.

  const handlePreviewAttachment = async (attachment) => {
        try {
            if (!attachment.bucket_name || !attachment.storage_path) {
                throw new Error('Attachment storage metadata is invalid for strict R2 download');
            }
            return await previewPrivateObject({
                axiosInstance,
                attachmentId: attachment.id,
            });
        } catch (error) {
            console.error('Error previewing attachment:', error);
        }
    };

  // -------------------------------------------------
  // Step 2 : Traveler Details
  // -------------------------------------------------

  const handleTravelersChange = (updater) => {
    const next = typeof updater === "function" ? updater(travelers) : updater;
    if (isOrderCancelledOrFailed) {
      if (next.length !== travelers.length) return;
      const hasContentChanges = next.some((t, i) => {
        const orig = travelers[i];
        if (!orig) return true;
        return Object.keys(t).some((k) => k !== "expanded" && t[k] !== orig[k]);
      });
      if (hasContentChanges) return;
    }
    if (isStructureLocked && next.length !== travelers.length) return;
    setTravelers(next);
  };


  // -------------------------------------------------
  // Step 1 : Select Trip
  // -------------------------------------------------

  const fetchTaxes = async () => {
    try {
      const response = await axiosInstance.get('/taxes/active');
      setTaxes(response.data);

    } catch (err) {
      console.error(`Unable to retrieve taxes`, err);
    }
  };

    useEffect(() => {
        const ids = new Set();
        tripPackages.forEach((pkg) => {
            (pkg.departure_dates || []).forEach((dep) => {
                const id = dep.hss_template_id ?? dep.hss_template;
                if (id) ids.add(id);
            });
        });
        if (selectedDepartureDate) {
            const depHssId = selectedDepartureDate.hss_template_id ?? selectedDepartureDate.hss_template;
            if (depHssId) ids.add(depHssId);
        }
        const idList = Array.from(ids);

        if (!idList.length) {
            setHssTemplateMap({});
            return;
        }

        let cancelled = false;
        const loadHssTemplates = async () => {
            setIsLoadingHssTemplates(true);
            try {
                const res = await axiosInstance.get(`/hss?ids=${idList.join(",")}`);
                const templates = Array.isArray(res.data) ? res.data : [];
                if (!cancelled) {
                    const map = {};
                    templates.forEach((t) => { map[t.id] = t; });
                    setHssTemplateMap(map);
                }
            } catch (err) {
                if (!cancelled) setHssTemplateMap({});
            } finally {
                if (!cancelled) setIsLoadingHssTemplates(false);
            }
        };
        loadHssTemplates();
        return () => { cancelled = true; };
    }, [tripPackages, selectedDepartureDate]);

  // Whenever departure after filter applied

  function updateTravelAfterDate(date) {
    if (!date) {
      setFieldError('travelAfterDate', null);
      setFieldError('travelBeforeDate', null);
      setTravelAfterDate(date);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const enteredDate = new Date(date);
    enteredDate.setHours(0, 0, 0, 0);

    const dateTo = new Date(travelBeforeDate);
    dateTo.setHours(0, 0, 0, 0);

    if (enteredDate < today) {
      setFieldError('travelAfterDate', 'Date can\'t be before today');
    } else if (travelBeforeDate && enteredDate > dateTo) {
      setFieldError('travelAfterDate', 'Date must be before the "To" date');
    }else {
      setFieldError('travelAfterDate', null);
      setFieldError('travelBeforeDate', null);
    }

    setTravelAfterDate(date);
  }

  // Whenever departure before filter applied

  function updateTravelBeforeDate(date) {
    if (!date) {
      setFieldError('travelBeforeDate', null);
      setFieldError('travelAfterDate', null);
      setTravelBeforeDate(date);
      return;
    }

    const dateFrom = new Date(travelAfterDate);
    dateFrom.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const enteredDate = new Date(date);
    enteredDate.setHours(0, 0, 0, 0);

    if (travelBeforeDate && enteredDate < dateFrom) {
      setFieldError('travelBeforeDate', 'Date must be after the "From" date');
    } else if (enteredDate < today) {
        setFieldError('travelBeforeDate', 'Date can\'t be before today');
    } else {
      setFieldError('travelBeforeDate', null);
      setFieldError('travelAfterDate', null);
    }

    setTravelBeforeDate(date);
  }

  // Reset all the data populated by trip customization step

  const resetTripCustomization = useCallback(() => {
    setSelectedAccommodationIds([]);
    setSelectedAddonIds([]);
    setAddonTravelerMap({});
    setAccommodationTravelerMap({});
    setInvalidAddonIds([]);
    setInvalidAccommodationIds([]);
  }, []);

  // -------------------------------------------------
  // Step 2 : Traveler Details
  // -------------------------------------------------

  // Retrieve seats available for all departure date that belongs to the selected trip

  useEffect(() => {
    if (activeStep !== 1 || !selectedDepartureDate?.id) return;

    const depId = selectedDepartureDate.id;
    const cachedValue = allocationMap[depId];
    const hasCachedValue = cachedValue !== undefined;

    if (hasCachedValue) {
      setSecureAllocation(cachedValue);
      setSeatsError("");
    } else {
      setIsLoadingSeats(true);
    }

    let cancelled = false;
    const refreshSecuredCount = async () => {
      try {
        const res = await axiosInstance.get("/travel-orders/secured-allocation", {
            params: { departure_date_id: depId, exclude_order_id: orderRecordId || undefined },
        });
        if (!cancelled) {
          const fresh = res.data?.secured_allocation ?? 0;
          setSecureAllocation(fresh);
          setAllocationMap((prev) => ({ ...prev, [depId]: fresh }));
          setSeatsError("");
        }
      } catch (err) {
        if (!cancelled && !hasCachedValue) {
          setSeatsError("Failed to load seat availability. Please try again.");
          setSecureAllocation(0);
        }
      } finally {
        if (!cancelled) setIsLoadingSeats(false);
      }
    };

    refreshSecuredCount();
    return () => {
      cancelled = true;
    };
  }, [activeStep, selectedDepartureDate?.id]);

  // Evaluate seats left for the selected departure date.

  const seatsLeft = useMemo(() => {
    const totalAllocation = selectedDepartureDate?.total_allocation ?? 0;
    return Math.max(totalAllocation - secureAllocation, 0);
  }, [selectedDepartureDate, secureAllocation]);

  // -------------------------------------------------
  // Step 3 : Trip Customization
  // -------------------------------------------------

  // Handle Accommodation selection

  const handleAccommodationToggle = (accommodationId) => {
    if (isOrderCancelledOrFailed || lockedAccommodationIds.includes(accommodationId)) return;
    setSelectedAccommodationIds((prev) => {
      const isRemoving = prev.includes(accommodationId);
      if (isRemoving) {
        setAccommodationTravelerMap((map) => {
          const { [accommodationId]: _removed, ...rest } = map;
          return rest;
        });
        setInvalidAccommodationIds((ids) => ids.filter((id) => id !== accommodationId));
        return prev.filter((id) => id !== accommodationId);
      }
      return [...prev, accommodationId];
    });
  };

  // Handle addition removal of traveler to the selected accommodation

  const handleAccommodationTravelersChange = (accommodationId, travelerIds) => {
    if (isOrderCancelledOrFailed) return;
    const activePaxClientIds = new Set(travelers.filter((t) => !t.isCancelled).map((t) => t.id));
    const lockedPax = lockedAccommodationTravelerMap[accommodationId] || [];
    const validTravelerIds = travelerIds.filter((id) => lockedPax.includes(id) || activePaxClientIds.has(id));
    if (lockedAccommodationIds.includes(accommodationId)) {
      const merged = Array.from(new Set([...lockedPax, ...validTravelerIds]));
      setAccommodationTravelerMap((prev) => ({ ...prev, [accommodationId]: merged }));
      return;
    }
    setAccommodationTravelerMap((prev) => ({ ...prev, [accommodationId]: validTravelerIds }));
    if (validTravelerIds.length > 0) {
      setInvalidAccommodationIds((prev) => prev.filter((id) => id !== accommodationId));
    }
  };

  // Handle Add-ons selection

  const handleAddonToggle = (addonId) => {
    if (isOrderCancelledOrFailed || lockedAddonIds.includes(addonId)) return;
    setSelectedAddonIds((prev) => {
      const isRemoving = prev.includes(addonId);
      if (isRemoving) {
        setAddonTravelerMap((map) => {
          const { [addonId]: _removed, ...rest } = map;
          return rest;
        });
        setInvalidAddonIds((prev) => prev.filter((id) => id !== addonId));
        return prev.filter((id) => id !== addonId);
      }
      return [...prev, addonId];
    });
  };

  const travelerCount = travelers.length;
    const tripNights = useMemo(
        () => getTripNights(selectedDepartureDate?.departure_date, selectedDepartureDate?.return_date, selectedTripPackage?.duration),
        [selectedDepartureDate, selectedTripPackage]
    );

  // Handle addition/removal of traveler to the selected add-on

  const handleAddonTravelersChange = (addonId, travelerIds) => {
    if (isOrderCancelledOrFailed) return;
    const activePaxClientIds = new Set(travelers.filter((t) => !t.isCancelled).map((t) => t.id));
    const lockedPax = lockedAddonTravelerMap[addonId] || [];
    const validTravelerIds = travelerIds.filter((id) => lockedPax.includes(id) || activePaxClientIds.has(id));
    if (lockedAddonIds.includes(addonId)) {
      const merged = Array.from(new Set([...lockedPax, ...validTravelerIds]));
      setAddonTravelerMap((prev) => ({ ...prev, [addonId]: merged }));
      return;
    }
    setAddonTravelerMap((prev) => ({ ...prev, [addonId]: validTravelerIds }));
    if (validTravelerIds.length > 0) {
      setInvalidAddonIds((prev) => prev.filter((id) => id !== addonId)); // NEW
    }
  };

  // -------------------------------------------------
  // Step 4 : Review & Payment
  // -------------------------------------------------

    // Load the Active cancellation terms so the Review step can show the refund policy.
    useEffect(() => {
        if (activeStep !== 3) return;
        let cancelled = false;
        const loadPolicies = async () => {
            setIsLoadingPolicies(true);
            try {
                const res = await axiosInstance.get("/payment-terms/", { params: { limit: 500 } });
                if (!cancelled) {
                    setPaymentTermPolicies(
                        (res.data || []).filter(
                            (t) => t.category === CANCELLATION_CATEGORY && t.status === "Active"
                        )
                    );
                }
            } catch (err) {
                console.error("Unable to retrieve payment term policies:", err);
                if (!cancelled) setPaymentTermPolicies([]); // section simply stays hidden
            } finally {
                if (!cancelled) setIsLoadingPolicies(false);
            }
        };
        loadPolicies();
        return () => {
            cancelled = true;
        };
    }, [activeStep, axiosInstance]);

    // Resolve a saved add-on id straight from the package, bypassing departure allocation filtering.
    const findAddonItem = useCallback((addonId) => {
        const addon = selectedTripPackage?.addons?.find((a) => a.id === addonId);
        if (!addon) return null;
        const linkedService = selectedTripPackage?.services?.find(
            (s) => s.id === addon.linked_component_id
        ) || null;
        // Linked add-ons still need the component row; AE/SB are standalone.
        if (addon.linked_component_id != null && !linkedService) return null;
        return { addon, linkedService, category: inferAddonCategory(addon, linkedService) };
    }, [selectedTripPackage]);

    // In edit mode, keep saved add-ons even if the current offerings hide them.
    const resolveSelected = useCallback((offered, ids) => {
        const selected = offered.filter((i) => ids.includes(i.addon.id));
        if (!isEditMode && !isStructureLocked) return selected;

        const have = new Set(offered.map((i) => i.addon.id));
        const extra = ids
            .filter((id) => !have.has(id))
            .map(findAddonItem)
            .filter(Boolean);
        return [...selected, ...extra];
    }, [isEditMode, isStructureLocked, findAddonItem]);

  // Helper func to switch to editing certain step

  const handleEditFromReview = (stepIndex) => {
    setIsEditingFromReview(true);
    setActiveStep(stepIndex);
    setStepError("");
  };

  // Retrieve active payment templates for the selected trip

    useEffect(() => {
        if (!selectedTripPackage?.id || !selectedDepartureDate?.id) {
            setPaymentTemplates([]);
            setSelectedPaymentTemplateId(null);
            return;
        }
        let cancelled = false;
        const loadTemplates = async () => {
            setIsLoadingTemplates(true);
            setTemplatesError("");
            try {
                const res = await axiosInstance.get("/payment-templates", {
                    params: {
                        trip_package_id: selectedTripPackage.id,
                        departure_date_id: selectedDepartureDate.id,
                        status: "active",
                    },
                });
                const templates = Array.isArray(res.data) ? res.data : [];
                const allowedIds = selectedTripPackage.payment_template_ids || [];
                const filtered = allowedIds.length
                    ? templates.filter((t) => allowedIds.includes(t.id))
                    : templates;

                if (!cancelled) {
                    setPaymentTemplates(filtered);
                    setSelectedPaymentTemplateId((prev) => {
                        if (prev && filtered.some((t) => t.id === prev)) return prev;
                        return filtered.length === 1 ? filtered[0].id : null;
                    });
                }
            } catch (err) {
                if (!cancelled) {
                    setTemplatesError("Failed to load payment plans for this package.");
                    setPaymentTemplates([]);
                }
            } finally {
                if (!cancelled) setIsLoadingTemplates(false);
            }
        };
        loadTemplates();
        fetchTaxes();
        return () => { cancelled = true; };
    }, [selectedTripPackage?.id, selectedDepartureDate?.id]);

  // Handle payment template selection

  const handleSelectPaymentTemplate = (templateId) => {
    if (isStructureLocked || isOrderCancelledOrFailed) return;
    setSelectedPaymentTemplateId(templateId);
    setInvalidPaymentTemplate(false);
  };

  // Retrieve selected accommodation upgrades.

    const selectedAccommodations = useMemo(
        () => resolveSelected(departureOfferings.accommodation_addons, selectedAccommodationIds),
        [departureOfferings, selectedAccommodationIds, resolveSelected]
    );

  // Retrieve selected add-ons upgrades.

    const selectedAddons = useMemo(
        () => resolveSelected(departureOfferings.other_addons, selectedAddonIds),
        [departureOfferings, selectedAddonIds, resolveSelected]
    );

    const lockedAccommodations = useMemo(
        () => resolveSelected(departureOfferings.accommodation_addons, lockedAccommodationIds),
        [departureOfferings, lockedAccommodationIds, resolveSelected]
    );
    const lockedAddons = useMemo(
        () => resolveSelected(departureOfferings.other_addons, lockedAddonIds),
        [departureOfferings, lockedAddonIds, resolveSelected]
    );

// What the ORIGINAL invoice covers. Unlocked: the current selection. Locked: invoiced lines minus supplements.
    const baselineAccommodations = useMemo(
        () => isSupplementMode
            ? lockedAccommodations.filter((i) => !supplementAddonIds.includes(i.addon.id))
            : selectedAccommodations,
        [isSupplementMode, lockedAccommodations, selectedAccommodations, supplementAddonIds]
    );
    const baselineAddons = useMemo(
        () => isSupplementMode
            ? lockedAddons.filter((i) => !supplementAddonIds.includes(i.addon.id))
            : selectedAddons,
        [isSupplementMode, lockedAddons, selectedAddons, supplementAddonIds]
    );

    const currency = selectedTripPackage?.currency

    const allAccommodations = useMemo(
        () => resolveSelected(departureOfferings.accommodation_addons, [...selectedAccommodationIds, ...lockedAccommodationIds]),
        [departureOfferings, selectedAccommodationIds, lockedAccommodationIds, resolveSelected]
    );
    const allAddons = useMemo(
        () => resolveSelected(departureOfferings.other_addons, [...selectedAddonIds, ...lockedAddonIds]),
        [departureOfferings, selectedAddonIds, lockedAddonIds, resolveSelected]
    );

    const supplementAccommodations = useMemo(() => {
        if (!isSupplementMode) return [];
        return allAccommodations.filter((i) => {
            const assigned = accommodationTravelerMap[i.addon.id] || [];
            const locked = lockedAccommodationTravelerMap[i.addon.id] || [];
            return assigned.some((ref) => !locked.includes(ref));
        });
    }, [isSupplementMode, allAccommodations, accommodationTravelerMap, lockedAccommodationTravelerMap]);

    const supplementAddons = useMemo(() => {
        if (!isSupplementMode) return [];
        return allAddons.filter((i) => {
            const assigned = addonTravelerMap[i.addon.id] || [];
            const locked = lockedAddonTravelerMap[i.addon.id] || [];
            return assigned.some((ref) => !locked.includes(ref));
        });
    }, [isSupplementMode, allAddons, addonTravelerMap, lockedAddonTravelerMap]);

    // New, uninvoiced picks: priced on their own, tax on their own subtotal
    const supplement = useMemo(() => {
        const lines = [
            ...supplementAccommodations.map((i) => {
                const assigned = accommodationTravelerMap[i.addon.id] || [];
                const locked = lockedAccommodationTravelerMap[i.addon.id] || [];
                const newRefs = assigned.filter((ref) => !locked.includes(ref));
                const count = newRefs.length;
                return {
                    key: `acc-${i.addon.id}`,
                    label: `Accommodation (${i.linkedService.name})`,
                    detail: `${formatAmount(i.addon.addon_price, currency)} × ${count} traveler${count === 1 ? "" : "s"}`,
                    amount: calculateAddonAmount(i.addon, count),
                };
            }),
            ...supplementAddons.map((i) => {
                const assigned = addonTravelerMap[i.addon.id] || [];
                const locked = lockedAddonTravelerMap[i.addon.id] || [];
                const newRefs = assigned.filter((ref) => !locked.includes(ref));
                const count = newRefs.length;
                const fixed = (i.addon.pricing_basis || "per_passenger") === "fixed";
                return {
                    key: `addon-${i.addon.id}`,
                    label: i.addon.name,
                    detail: fixed
                        ? "Fixed price"
                        : `${formatAmount(i.addon.addon_price, currency)} × ${count} traveler${count === 1 ? "" : "s"}`,
                    amount: calculateAddonAmount(i.addon, count),
                };
            }),
        ];
        const subtotal = lines.reduce((s, l) => s + l.amount, 0);
        const taxLines = taxes.map((t) => ({
            key: t.id || t.tax_type,
            label: `${t.tax_type} (${t.rate}%)`,
            amount: subtotal * (t.rate / 100),
        }));
        const total = subtotal + taxLines.reduce((s, t) => s + t.amount, 0);
        return { lines, subtotal, taxLines, total };
    }, [supplementAccommodations, supplementAddons, accommodationTravelerMap, lockedAccommodationTravelerMap, addonTravelerMap, lockedAddonTravelerMap, tripNights, taxes, currency]);

  // Retrieve selected payment template

  const selectedPaymentTemplate = useMemo(
      () => paymentTemplates.find((t) => t.id === selectedPaymentTemplateId) || null,
      [paymentTemplates, selectedPaymentTemplateId]
  );

  // Retrieve pricing

    const reviewPricing = useMemo(() => {
        if (!selectedTripPackage) {
            return { subtotal: 0, accommodationAmount: 0, addonAmount: 0, hssSurchargeAmount: 0, isHssLive: false, total: 0, amountPayable: 0, remainingAmount: 0, paymentSchedule: [] };
        }
        return buildReviewPricing({
            baseSubtotal: selectedTripPackage.base_price * travelerCount,
            selectedAccommodations: baselineAccommodations,
            selectedAddons: baselineAddons,
            accommodationTravelerMap: isSupplementMode ? lockedAccommodationTravelerMap : accommodationTravelerMap,
            addonTravelerMap: isSupplementMode ? lockedAddonTravelerMap : addonTravelerMap,
            tripNights,
            paymentTemplate: selectedPaymentTemplate,
            selectedDepartureDate,
            hssTemplate: selectedHssTemplate,
            taxes,
            travelerCount,
        });
    }, [selectedTripPackage, travelerCount, baselineAccommodations, isSupplementMode, lockedAccommodationTravelerMap, accommodationTravelerMap, baselineAddons, lockedAddonTravelerMap, addonTravelerMap, tripNights, selectedPaymentTemplate, selectedDepartureDate, selectedHssTemplate, taxes]);

  // Load enabled payment gateways on the review/payment step (AC1 / AC10).

  useEffect(() => {
    if (activeStep !== 3) return;
    let cancelled = false;
    const loadGateways = async () => {
      setIsLoadingGateways(true);
      setGatewayError("");
      try {
        const res = await axiosInstance.get("/payment-gateways/availability");
        if (cancelled) return;
        setGatewayAvailability(res.data);
        const { stripe, razorpay } = res.data || {};
        if (stripe && !razorpay) {
          setSelectedGateway("stripe");
        } else if (razorpay && !stripe) {
          setSelectedGateway("razorpay");
        } else if (!stripe && !razorpay) {
          setSelectedGateway("");
        }
      } catch (err) {
        if (!cancelled) {
          setGatewayError(
            err?.response?.data?.detail || "Unable to load payment gateways."
          );
        }
      } finally {
        if (!cancelled) setIsLoadingGateways(false);
      }
    };
    loadGateways();
    return () => {
      cancelled = true;
    };
  }, [activeStep, axiosInstance]);

    const handleSupplementPay = async () => {
        if (!selectedGateway) { setPaymentError("Please select Stripe or Razorpay to continue."); return; }
        if (!supplementAccommodations.length && !supplementAddons.length) {
            setPaymentError("Select at least one new add-on to continue.");
            return;
        }
        setPaymentError("");
        setIsProcessingPayment(true);
        try {
            // Reopen the existing link if the modal was closed without sending
            if (supplementInvoiceRef.current) {
                setPaymentLinkInvoiceId(supplementInvoiceRef.current);
                setPaymentLinkModalOpen(true);
                return;
            }

            const lines = [...supplementAccommodations, ...supplementAddons].map((item) => {
                const isAcc = item.linkedService?.category === "accommodation";
                const map = isAcc ? accommodationTravelerMap : addonTravelerMap;
                const lockedMap = isAcc ? lockedAccommodationTravelerMap : lockedAddonTravelerMap;
                const assigned = map[item.addon.id] || [];
                const locked = lockedMap[item.addon.id] || [];
                const newRefs = assigned.filter((ref) => !locked.includes(ref));
                return {
                    addon_id: item.addon.id,
                    service_id: item.linkedService?.id ?? null,
                    category: item.linkedService?.category ?? item.category ?? null,
                    traveler_ids: newRefs
                        .map((cid) => travelers.find((t) => t.id === cid && !t.isCancelled)?.server_id)
                        .filter(Boolean),
                };
            });

            const res = await axiosInstance.post(
                `/travel-orders/${orderRecordId}/addon-supplements`,
                { payment_gateway: selectedGateway, addons: lines }
            );
            supplementInvoiceRef.current = res.data.invoice_id;

            // Refetch: the new lines come back with invoice_id/supplement_id, so they lock
            const fresh = await axiosInstance.get(`/travel-orders/${orderRecordId}/edit`);
            setEditOrderData(fresh.data);

            const primary = travelers.find((t) => t.isPrimary) || travelers[0];
            setPaymentLinkDefaultEmail(primary?.email || "");
            setPaymentLinkInvoiceId(res.data.invoice_id);
            setPaymentLinkModalOpen(true);
        } catch (err) {
            const message = err?.response?.data?.detail || err?.message || "Unable to create add-on payment link.";
            setPaymentError(typeof message === "string" ? message : JSON.stringify(message));
        } finally {
            setIsProcessingPayment(false);
        }
    };

  // Handle payment on last step — create travel order, then open email popup with payment link.

  const handlePayNow = async () => {
    if (isOrderCancelledOrFailed) return;
    if (isSupplementMode) return handleSupplementPay();
    if (!selectedPaymentTemplateId) {
      setInvalidPaymentTemplate(true);
      setPaymentError("Please select a payment plan to continue.");
      return;
    }
    if (!selectedGateway) {
      setPaymentError("Please select Stripe or Razorpay to continue.");
      return;
    }
    setInvalidPaymentTemplate(false);
    setPaymentError("");
    setIsProcessingPayment(true);
    setBookingStatus("Payment Pending");

    try {
      // Reuse the same order on retry (network / pending-payment failure) — never
      // create a duplicate booking when orderRecordId is already known.
      const created = await submitWizardOrder({
        // Stay Draft until Deposit/Full Payment is actually paid. Secured is
        // set by payment sync, which also creates MOLs + merchant emails.
        status: "Draft",
        subStatus: "Payment Pending",
      });
      setLatestOrderId(created.order_id);
      setCreatedOrderNumericId(created.id);

      const pendingRes = await axiosInstance.get(
        `/payment-gateways/travel-orders/${created.id}/pending-payment`
      );
      if (!pendingRes.data?.invoice_id) {
        throw new Error(
          "No payable invoice was created for this booking. Check the payment plan and try again."
        );
      }

      const primary = travelers.find((t) => t.isPrimary) || travelers[0];
      setPaymentLinkDefaultEmail(primary?.email || "");
      setPaymentLinkInvoiceId(pendingRes.data.invoice_id);
      setPaymentLinkModalOpen(true);
    } catch (err) {
      setBookingStatus("Draft")
      const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to prepare payment link. Please try again.";
      setPaymentError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePaymentLinkSent = () => {
    setPaymentLinkModalOpen(false);
    setCompletedSteps((prev) => new Set([...prev, 3]));
    setActiveStep(4);
    setSnackbar({
      open: true,
      message: `Payment link sent — order ${latestOrderId || createdOrderNumericId || ""}.`,
      severity: 'success'
    });
  };

  const handleResendPaymentLink = async () => {
    if (isOrderCancelledOrFailed) return;
    const orderId = createdOrderNumericId || orderRecordId;
    if (!orderId) {
      setPaymentError("Order is not ready yet. Go back and send the payment link again.");
      setActiveStep(3);
      return;
    }

    if (supplementInvoiceRef.current) {
      setPaymentLinkInvoiceId(supplementInvoiceRef.current);
      setPaymentLinkModalOpen(true);
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError("");
    try {
      const pendingRes = await axiosInstance.get(
        `/payment-gateways/travel-orders/${orderId}/pending-payment`
      );
      if (!pendingRes.data?.invoice_id) {
        throw new Error("No pending payment found for this booking.");
      }
      const primary = travelers.find((t) => t.isPrimary) || travelers[0];
      setPaymentLinkDefaultEmail(primary?.email || "");
      setPaymentLinkInvoiceId(pendingRes.data.invoice_id);
      setPaymentLinkModalOpen(true);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to open payment link. Please try again.";
      setSnackbar({
        open: true,
        message: typeof message === "string" ? message : JSON.stringify(message),
        severity: "error",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // -------------------------------------------------
  // Editing Existing Order
  // -------------------------------------------------

  const [isLoadingOrderForEdit, setIsLoadingOrderForEdit] = useState(isEditMode);

    useEffect(() => {
        if (!isEditMode || !tripPackages.length || !editOrderData) return;
        let cancelled = false;

        const loadOrderForEdit = async () => {
            setIsLoadingOrderForEdit(true);
            try {
                const data = editOrderData;
                if (cancelled) return;

                const tripPackage = tripPackages.find((p) => p.id === data.trip_package_id);

                if (!tripPackage) {
                    setStepError("Could not find the trip package associated with this order. It may have been removed.");
                    return;
                }

                setSelectedTripPackage(tripPackage);

                const departureOption =
                    (tripPackage.departure_dates || []).find((d) => d.id === data.departure_date_id) || null;
                setSelectedDepartureDate(departureOption);

        // Build travelers, tracking server id -> client id so addon refs can be remapped below
        const serverIdToClientId = {};
        const hydratedTravelers = (data.travelers || []).map((t) => {
          const clientId = `existing-${t.id}`;
          serverIdToClientId[t.id] = clientId;
          return {
            ...createEmptyTraveler(Boolean(t.is_primary), true),
            id: clientId,
            accountMode: t.account_id ? "existing" : "",
            accountId: t.account_id || null,
            contactMode: t.contact_id ? "existing" : "",
            contactId: t.contact_id || null,
            server_id: t.id,
            first_name: t.first_name || "",
            middle_name: t.middle_name || "",
            last_name: t.last_name || "",
            email: t.email || "",
            phoneCountryCode: t.dial_code || "",
            phone: t.mobile || "",
            gender: t.gender || "",
            nationality: t.nationality || "",
            address: t.address || "",
            country: t.country || "",
            passportNumber: t.passport_number || "",
            dateOfBirth: parseApiDate(t.date_of_birth),
            passportExpiry: parseApiDate(t.passport_expiry_date),
            passportIssueDate: parseApiDate(t.passport_issue_date),
            passportLocation: t.passport_location || "",
            specialDetails: t.special_request || "",
            isPrimary: Boolean(t.is_primary),
            isCancelled: Boolean(t.is_pax_cancelled),
          };
        });
        setTravelers(hydratedTravelers);

        // Addons — remap traveler_client_refs (server pax ids) -> our new client ids
        const s = String(data.status || "Draft").trim().toLowerCase();
        const sub = String(data.sub_status || "Draft").trim().toLowerCase();
        const isPureDraft = s === "draft" && (sub === "draft" || sub === "") && !data.has_payment_schedule;
        const orderLocked = !isPureDraft;
        const accommodationIds = [], addonIds = [];
        const lockedAcc = [], lockedAdd = [], suppIds = [];
        const accMap = {}, addonMap = {};
        const lockedAccMap = {}, lockedAddMap = {};

        (data.addons || []).forEach((a) => {
            const refs = (a.traveler_client_refs || [])
                .map((serverId) => serverIdToClientId[serverId])
                .filter(Boolean);
            const isAcc = a.category === "accommodation";
            const isLocked = orderLocked;

            if (isLocked) {
                (isAcc ? lockedAcc : lockedAdd).push(a.addon_id);
                (isAcc ? lockedAccMap : lockedAddMap)[a.addon_id] = [...refs];
                if (a.is_supplementary || a.supplement_id != null) suppIds.push(a.addon_id);
            } else {
                (isAcc ? accommodationIds : addonIds).push(a.addon_id);
            }
            // maps hold BOTH locked and new, so locked cards show their travelers
            (isAcc ? accMap : addonMap)[a.addon_id] = refs;
        });

        setSelectedAccommodationIds(accommodationIds);
        setSelectedAddonIds(addonIds);
        setLockedAccommodationIds(lockedAcc);
        setLockedAddonIds(lockedAdd);
        setLockedAccommodationTravelerMap(lockedAccMap);
        setLockedAddonTravelerMap(lockedAddMap);
        setSupplementAddonIds(suppIds);
        setAccommodationTravelerMap(accMap);
        setAddonTravelerMap(addonMap);

        // Hydrate gateway selection for edit mode using the saved order payment method.
        // Legacy orders may only have payment_method (Card/UPI) without payment_gateway.
        const method = String(data.payment_method || "").toLowerCase();
        const gateway = String(data.payment_gateway || "").toLowerCase();
        if (gateway === "stripe" || gateway === "razorpay") {
          setSelectedGateway(gateway);
        } else if (method === "upi") {
          setSelectedGateway("razorpay");
        } else if (method === "card") {
          setSelectedGateway("stripe");
        }
        setSelectedPaymentTemplateId(data.payment_template_id || null);
        setIsStructureLocked(orderLocked);

        // Preserve original status/sub_status — the Save handler sends these back
        // unchanged so editing an order never silently re-triggers "payment taken"
        setOriginalOrderStatus(data.status);
        const fullyLockedStatuses = ["cancelled", "cancellation", "failed", "travel in progress", "travel completed"];
        const fullyLockedSubStatuses = [
            "travel in progress",
            "travel completed",
            "cancelled by customer",
            "cancelled by organization",
            "cancelled - seats unavailable (refunded)",
            "failed",
            "payment failed",
        ];
        if (fullyLockedStatuses.includes(s) || fullyLockedSubStatuses.includes(sub)) {
            setStepError("This booking can no longer be edited.");
        }
        setOriginalOrderSubStatus(data.sub_status);
        setBookingStatus(data.sub_status || "Draft");

        setCompletedSteps(new Set([0, 1, 2]));
      } catch (err) {
        setStepError("Failed to load order details for editing. Please try again.");
      } finally {
        if (!cancelled) setIsLoadingOrderForEdit(false);
      }
    };

    loadOrderForEdit();
    return () => {
      cancelled = true;
    };
}, [isEditMode, editOrderData, tripPackages]);

  // -------------------------------------------------
  // Stepper
  // -------------------------------------------------

  const handleStepClick = (index) => {
    // While editing an existing order, allow direct jump to any stage —
    // but not before the package/order hydrate finishes, and not to steps
    // that require a selected package when one isn't available yet.
    if (isEditMode && isLoadingOrderForEdit) return;
    if (index > 0 && !selectedTripPackage) {
      setStepError("Please select a trip package before continuing.");
      setActiveStep(0);
      return;
    }
    if (isOrderCancelledOrFailed || isEditMode || index <= activeStep || completedSteps.has(index)) {
      setActiveStep(index);
      setStepError("");
    }
  };

  const buildWizardPayload = useCallback(
      ({ status, subStatus }) => {
        const primary = travelers.find((t) => t.isPrimary) || travelers[0];
        const allSelectedAddonItems = isStructureLocked
          ? [...lockedAccommodations, ...lockedAddons]
          : [...baselineAccommodations, ...baselineAddons];
        const addonsPayload = allSelectedAddonItems
          .map((item) => {
            const isAccommodation = item.linkedService?.category === "accommodation";
            const travelerMap = isAccommodation ? accommodationTravelerMap : addonTravelerMap;
            const assignedTravelerClientRefs = travelerMap[item.addon.id] || [];
            return {
              addon_id: item.addon.id,
              service_id: item.linkedService?.id ?? null,
              category: item.linkedService?.category ?? item.category ?? null,
              traveler_client_refs: assignedTravelerClientRefs,
            };
          })
          .filter((item) => item.traveler_client_refs.length > 0);

        const basePrice = selectedTripPackage.base_price;
        const offerWindow = getOfferWindow(selectedDepartureDate);
        const rawDiscountPercentage = selectedDepartureDate?.discount_percentage || 0;
        const isDiscountLive = rawDiscountPercentage > 0 && (!offerWindow || offerWindow.isActive);
        const discountPercentage = isDiscountLive ? rawDiscountPercentage : 0;
        const discountedPrice = basePrice - (basePrice * discountPercentage) / 100;

        return {
          trip_package_id: selectedTripPackage.id,
          departure_date_id: selectedDepartureDate?.id,
          channel: "Manual",
          payment_method: GATEWAY_PAYMENT_METHOD[selectedGateway] || "Card",
          order_payment_type: "Deposit",
          payment_template_id: selectedPaymentTemplateId,
          primary_contact_email: primary?.email,
          primary_contact_phone: primary?.phone,
          order_total: reviewPricing.total,
          deposit_amount: reviewPricing.amountPayable,
          pending_amount: reviewPricing.remainingAmount,
          status: status || "Draft",
          sub_status: subStatus || "Draft",
          no_of_installments:
            selectedPaymentTemplate?.payment_type === "deposit_installments"
              ? Math.max(
                  0,
                  (reviewPricing.paymentSchedule || []).filter((row) =>
                    String(row.label || "").startsWith("Installment")
                  ).length
                )
              : null,
          unit_price: discountedPrice,
          travelers: travelers.map((t) => ({
            id: t.server_id || undefined,
            account_mode: t.isPrimary ? (t.accountMode || null) : null,
            account_id: t.isPrimary && t.accountMode === "existing" ? t.accountId : null,
            contact_mode: !t.isPrimary ? (t.contactMode || null) : null,
            contact_id: !t.isPrimary && t.contactMode === "existing" ? t.contactId : null,
            client_ref: t.id,
            first_name: t.first_name,
            middle_name: t.middle_name,
            last_name: t.last_name,
            email: t.email || null,
            dial_code: t.phoneCountryCode || null,
            mobile: t.phone || null,
            gender: t.gender || null,
            nationality: t.nationality || null,
            address: t.address || null,
            country: t.country || null,
            passport_number: t.passportNumber || null,
            passport_location: t.passportLocation || null,
            date_of_birth: toApiDate(t.dateOfBirth),
            passport_expiry_date: toApiDate(t.passportExpiry),
            passport_issue_date: toApiDate(t.passportIssueDate),
            special_request: t.specialDetails || null,
            is_primary: Boolean(t.isPrimary),
            departure_city: selectedDepartureDate?.departure_city || null,
            is_pax_cancelled: Boolean(t.isCancelled),
          })),
          addons: addonsPayload,
        };
      },
      [
        travelers,
        selectedTripPackage,
        selectedDepartureDate,
        selectedGateway,
        selectedPaymentTemplateId,
        reviewPricing,
        baselineAccommodations,
        baselineAddons,
        lockedAccommodations,
        lockedAddons,
        isStructureLocked,
        accommodationTravelerMap,
        addonTravelerMap,
      ]
  );

  const submitInFlight = useRef(false);
  const submitWizardOrder = async ({ status, subStatus }) => {
    if (isOrderCancelledOrFailed) {
      throw new Error("This travel order is cancelled or failed and cannot be modified.");
    }
    if (submitInFlight.current) throw new Error("A save is already in progress.");
    submitInFlight.current = true;
    try {
        if (!selectedDepartureDate?.id) {
            throw new Error(
                "Please select a departure date before continuing."
            );
        }
        const payload = buildWizardPayload({ status, subStatus });
        const response = orderRecordId
            ? await axiosInstance.put(`/travel-orders/wizard/${orderRecordId}`, payload)
            : await axiosInstance.post("/travel-orders/wizard", payload);

        const data = response.data;

        // Re-saves must update the same account instead of creating another one.
        const savedAccountId = data?.customer?.account_id;
        if (savedAccountId) {
            setTravelers((prev) =>
                prev.map((t) =>
                    t.isPrimary ? { ...t, accountMode: "existing", accountId: savedAccountId } : t
                )
            );
        }

        const links = data?.traveler_links || [];
        if (links.length) {
            // Exact mapping by client_ref
            const byRef = Object.fromEntries(links.map((l) => [l.client_ref, l]));
            setTravelers((prev) =>
                prev.map((t) => {
                    const link = byRef[t.id];
                    if (!link) return t;
                    return {
                        ...t,
                        server_id: link.pax_id,
                        ...(!t.isPrimary && link.contact_id
                            ? { contactMode: "existing", contactId: link.contact_id }
                            : {}),
                    };
                })
            );
        } else {
            // Fallback if the backend sent no links: assign unused ids in order
            const ids = (data.travelers || []).map((t) => t.id).sort((a, b) => a - b);
            setTravelers((prev) => {
                const used = new Set(prev.map((t) => t.server_id).filter(Boolean));
                const free = ids.filter((id) => !used.has(id));
                let k = 0;
                return prev.map((t) => (t.server_id ? t : { ...t, server_id: free[k++] }));
            });
        }

        // First successful POST establishes the id for every later save in this session
        if (!orderRecordId && data?.id) setOrderRecordId(data.id);
        return data;
    } finally {
        submitInFlight.current = false;
    }
  };

  const createOrderAndExit = async () => {
    if (isOrderCancelledOrFailed) return;
    try {
      setIsProcessingPayment(true);
      const created = await submitWizardOrder({
        status: "Draft",
        subStatus: "Draft",
      });
      setLatestOrderId(created.order_id);
      setCreatedOrderNumericId(created.id);
      setSnackbar({
        open: true,
        message: `Order successfully created - order id - ${created.order_id}.`,
        severity: 'success'
      });
      setIsEditingFromReview(false);
      setTimeout(() => {
        navigate(`/travel-order/${created.id}`);
      }, 800);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create travel order.";
      setStepError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Scroll to the element.

  const scrollToField = (fieldId) => {
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  const validateCurrentStep = () => {

    // Validating step 1

    if (activeStep === 0) {

      // Validating that a trip is selected.

      const tripPackageMissing = !selectedTripPackage;
      if (tripPackageMissing) {
        setFieldError("trip", "Please select a trip package to continue");
        scrollToField("select-trip-section");
        return false;
      } else {
        setFieldError("trip", null);
      }

      // Validating that departure date has been selected.

      const departureMissing = !selectedDepartureDate;
      if (departureMissing) {
        setFieldError("departureDate", "Please select a departure date to continue.");
        scrollToField("departure-date-section");
        return false;
      } else {
        setFieldError("departureDate", null);
      }

      // Validating that the departure date must have enough seats to fill in the configured traveler from step 2.

      const secured = allocationMap[selectedDepartureDate.id] ?? 0;
      const totalAllocation = selectedDepartureDate.total_allocation ?? 0;
      const seatsAvailable = Math.max(totalAllocation - secured, 0);
      if (!isStructureLocked && travelers.length > seatsAvailable) {
        setFieldError(
            "departureDate",
            `You have configured ${travelers.length} traveler${travelers.length === 1 ? "" : "s"}, but this departure ` +
            `date only has ${seatsAvailable} seat${seatsAvailable === 1 ? "" : "s"} left. Please select another departure date.`
        );
        scrollToField("departure-date-section");
        return false;
      } else {
        setFieldError("departureDate", null);
      }
    }

    // Validating step 2 - trigger internal validate func

    if (activeStep === 1) {
      const result = travelerStepRef.current?.validate();
      if (result && !result.isValid) {
        setStepError(result.message);
        if (result.firstErrorFieldDomId) {
          // slight delay so the accordion can finish expanding before we scroll
          setTimeout(() => scrollToField(result.firstErrorFieldDomId), 100);
        }
        return false;
      }
    }

    // Validating step 3 - Trip Customization

    if (activeStep === 2) {

        const seatsUsed = {};
        for (const item of selectedAddons) {
            const left = addonSeatsLeft[item.addon.id];
            if (left == null) continue;
            const serviceId = item.linkedService.id;
            seatsUsed[serviceId] = (seatsUsed[serviceId] || 0) + (addonTravelerMap[item.addon.id] || []).length;
            if (seatsUsed[serviceId] > left) {
                setInvalidAddonIds([item.addon.id]);
                setStepError(`Only ${left} seat${left === 1 ? "" : "s"} left for ${item.addon.name}. Remove a traveler or choose another add-on.`);
                setTimeout(() => scrollToField(`addon-travelers-${item.addon.id}`), 100);
                return false;
            }
        }

      // At least one traveler must be selected if adding any accommodation add-ons.

      const missingAccommodation = selectedAccommodationIds.filter(
          (id) => !(accommodationTravelerMap[id] && accommodationTravelerMap[id].length > 0)
      );
      if (missingAccommodation.length > 0) {
        setInvalidAccommodationIds(missingAccommodation);
        setStepError("Please select at least one traveler for each selected accommodation.");
        setTimeout(() => scrollToField(`accommodation-travelers-${missingAccommodation[0]}`), 100);
        return false;
      }

      setInvalidAccommodationIds([]);

      // At least one traveler must be selected if adding any add-ons & experiences.

      const missingAddon = selectedAddonIds.filter((id) => {
        const travelerIds = addonTravelerMap[id] || [];
        return travelerIds.length < 1;
      });

      if (missingAddon.length > 0) {
        setInvalidAddonIds(missingAddon);
        setStepError("Please select at least one traveler for each selected add-on.");
        setTimeout(() => scrollToField(`addon-travelers-${missingAddon[0]}`), 100);
        return false;
      }

      // Enforce min/max quantity (Min Pax / Max Pax) from the master add-on
        const paxInvalid = selectedAddonIds.filter((id) => {
            const item = selectedAddons.find((x) => x.addon.id === id);
            if (!item) return false;
            const count = (addonTravelerMap[id] || []).length;
            const minQ = item.addon.min_quantity;
            const maxQ = item.addon.max_quantity;
            if (minQ != null && count < Number(minQ)) return true;
            if (maxQ != null && count > Number(maxQ)) return true;
            return false;
        });
        if (paxInvalid.length > 0) {
            setInvalidAddonIds(paxInvalid);
            const item = selectedAddons.find((x) => x.addon.id === paxInvalid[0]);
            const minQ = item?.addon?.min_quantity;
            const maxQ = item?.addon?.max_quantity;
            setStepError(
                `Traveler count for "${item?.addon?.name || "add-on"}" must be between ${minQ ?? 1} and ${maxQ ?? "∞"}.`
            );
            setTimeout(() => scrollToField(`addon-travelers-${paxInvalid[0]}`), 100);
            return false;
        }

      setInvalidAddonIds([]);
    }

    setStepError("");
    return true;
  };

    // Step 2: make sure the primary traveler's account can be created/updated (email uniqueness, name rules)
    // before the order is saved on step 3.
    const verifyPrimaryAccount = async () => {
        const primary = travelers.find((t) => t.isPrimary) || travelers[0];
        if (!primary?.accountMode || primary.isCancelled) return true;
        try {
            await axiosInstance.post("/travel-orders/primary-account/validate", {
                account_mode: primary.accountMode,
                account_id: primary.accountMode === "existing" ? primary.accountId : null,
                email: primary.email || null,
                first_name: primary.first_name,
                middle_name: primary.middle_name,
                last_name: primary.last_name,
            });
            return true;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const message = typeof detail === "string" ? detail : "Unable to verify the account details.";
            setStepError(message);
            const field = /email/i.test(message) ? "email" : "first_name";
            travelerStepRef.current?.showFieldError(primary.id, field, message);
            return false;
        }
    };

    // Same idea as the account check: surface contact email/name problems on the right traveler's fields
    // before the order is saved.
    const verifyTravelerContacts = async () => {
        const items = travelers
            .filter((t) => !t.isPrimary && t.contactMode && !t.isCancelled)
            .map((t) => ({
                client_ref: t.id,
                contact_mode: t.contactMode,
                contact_id: t.contactMode === "existing" ? t.contactId : null,
                email: t.email || null,
                first_name: t.first_name,
                middle_name: t.middle_name,
                last_name: t.last_name,
            }));
        if (!items.length) return true;
        const primary = travelers.find((t) => t.isPrimary) || travelers[0];
        try {
            const res = await axiosInstance.post("/travel-orders/traveler-contacts/validate", {
                primary_account_id: primary?.accountMode === "existing" ? primary.accountId : null,
                travelers: items,
            });
            const errors = res.data?.errors || [];
            if (!errors.length) return true;
            setStepError(errors[0].message);
            travelerStepRef.current?.showFieldErrors(
                errors.map((e) => ({ travelerId: e.client_ref, field: e.field, message: e.message }))
            );
            return false;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setStepError(typeof detail === "string" ? detail : "Unable to verify the contact details.");
            return false;
        }
    };

  const handleNext = async () => {
    if (isOrderCancelledOrFailed) {
      if (activeStep < WIZARD_STEPS.length - 1) {
        setActiveStep((prev) => prev + 1);
      }
      return;
    }
    if (!validateCurrentStep()) return;

      if (activeStep === 1 && !isAccountContactLocked && !(await verifyPrimaryAccount())) return;
      if (activeStep === 1 && !isAccountContactLocked && !(await verifyTravelerContacts())) return;

    // Save updated traveler information when editing existing order on hitting next
    if (activeStep === 1 && isEditMode) {
      setIsSavingStep(true);
      try {
        const saved = await submitWizardOrder({
          status: originalOrderStatus || "Draft",
          subStatus: originalOrderSubStatus || "Draft",
        });
        setLatestOrderId(saved.order_id);
        setCreatedOrderNumericId(saved.id);
        setSnackbar({
          open: true,
          message: "Traveler details saved successfully.",
          severity: "success",
        });
      } catch (err) {
        const message =
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to save traveler details. Please try again.";
        setStepError(typeof message === "string" ? message : JSON.stringify(message));
        setIsSavingStep(false);
        return; // don't advance if the save failed
      }
      setIsSavingStep(false);
    }

    // edit step 0/1/2 and coming forward again.
    if (activeStep === 2 && !isStructureLocked) {
      setIsSavingStep(true);
      try {
        const saved = await submitWizardOrder({
          // If this is an existing Secured order being edited, don't
          // silently downgrade it back to Draft - preserve its status.
          status: originalOrderStatus || "Draft",
          subStatus: originalOrderSubStatus || "Draft",
        });
        setLatestOrderId(saved.order_id);
        setCreatedOrderNumericId(saved.id);
      } catch (err) {
        const message =
            err?.response?.data?.detail ||
            err?.message ||
            "Failed to save trip details. Please try again.";
        setStepError(typeof message === "string" ? message : JSON.stringify(message));
        setIsSavingStep(false);
        return; // don't advance if the save failed
      }
      setIsSavingStep(false);
    }

    if (isEditingFromReview) {
      // send the user back to Review instead of saving immediately —
      // they should see the updated summary before confirming payment
      setCompletedSteps((prev) => new Set([...prev, activeStep]));
      setActiveStep(3);
      setIsEditingFromReview(false);
      setStepError("");
      return;
    }

    setCompletedSteps((prev) => new Set([...prev, activeStep]));
    if (activeStep < WIZARD_STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep((prev) => prev - 1);
  };

  const handleSaveAsDraft = async () => {
    setStepError("");
    await createOrderAndExit();
  };

  const handleSaveEdit = async () => {
    if (isOrderCancelledOrFailed) return;
    setIsProcessingPayment(true);
    setPaymentError("");
    try {
      const updated = await submitWizardOrder({
        status: originalOrderStatus,
        subStatus: originalOrderSubStatus,
      });
      setSnackbar({
        open: true,
        message: `Order ${updated.order_id} updated successfully.`,
        severity: 'success'
      });
      navigate(`/travel-order/${editOrderId}`);   // fixed: plural, matches your route
    } catch (err) {
      const message = err?.response?.data?.detail || err?.message || "Failed to update travel order.";
      setPaymentError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const handleCancelBooking = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleConfirmCancelBooking = () => {
    setCancelDialogOpen(false);
    if (isEditMode && (editOrderId || orderRecordId)) {
      navigate(`/travel-order/${editOrderId || orderRecordId}`);
    } else {
      navigate("/travel-orders");
    }
  };

  const nextLabel =
      isEditingFromReview
          ? "Back to Review"
          : activeStep === 0
              ? "Next: Traveler Details"
              : activeStep === 1
                  ? "Next: Trip Customization"
                  : activeStep === 2
                      ? "Next: Review & Payment"
                      : activeStep === WIZARD_STEPS.length - 1
                          ? "Return to Orders"
                          : `Next: ${WIZARD_STEPS[activeStep + 1]}`;

  const orderNumericId = createdOrderNumericId || orderRecordId;
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
            <SelectTripStep
                isEditMode={isSelectionLocked || isOrderCancelledOrFailed}
                readOnly={isOrderCancelledOrFailed}
                isLoadingTripPackages={isLoadingTripPackages}
                isLoadingOrderForEdit={isLoadingOrderForEdit}
                filteredTripPackages={filteredTripPackages}
                // Filter related params
                searchDeparture={searchDeparture}
                onSearchDepartureChange={setSearchDeparture}
                travelAfterDate={travelAfterDate}
                onTravelAfterDateChange={updateTravelAfterDate}
                travelBeforeDate={travelBeforeDate}
                onTravelBeforeDateChange={updateTravelBeforeDate}
                // Trip Package Selection
                selectedTripPackage={selectedTripPackage}
                onSelectTripPackage={handleSelectTripPackage}
                ClearPackageSelection={ClearPackageSelection}
                // Departure Date Selection
                selectedDepartureDate={selectedDepartureDate}
                allocationMap={allocationMap}
                onChangeDepartureDate={handleChangeDepartureDate}
                isLoadingAllocations={isLoadingAllocations}
                hssTemplateMap={hssTemplateMap}
                fieldErrors={fieldErrors}
            />
        );
      case 1:
        return (
            <>
                {isSupplementMode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        A payment link has already been generated for this booking, so travelers
                        can't be added or removed. You can still edit traveler details.
                    </Alert>
                )}
                <TravelerDetailsStep
                    ref={travelerStepRef}
                    seatsError={seatsError}
                    travelers={travelers}
                    departure={selectedDepartureDate}
                    onTravelersChange={handleTravelersChange}
                    maxTravelers={seatsLeft}
                    isLoadingSeats={isLoadingSeats}
                    readOnly={isOrderCancelledOrFailed}
                    isEditMode={isEditMode}
                    isStructureLocked={isSupplementMode}
                    isAccountContactLocked={isAccountContactLocked}
                />
            </>
        );
      case 2:
        if (!selectedTripPackage) {
          return (
            <Alert severity="info">
              Select a trip package first to customize accommodations and add-ons.
            </Alert>
          );
        }
        return (
            <>
                {isSupplementMode && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        A payment link was already generated, so existing add-ons are locked.
                        You can still add more; they'll be billed separately.
                    </Alert>
                )}
                <TripCustomizationStep
                    currency={selectedTripPackage?.currency}
                    baseAccommodation={departureOfferings.base_accommodation}
                    accommodations={departureOfferings.accommodation_addons}
                    baseAddons={departureOfferings.base_services}
                    addons={visibleOtherAddons}
                    addonSeatsLeft={addonSeatsLeft}
                    selectedAccommodationIds={selectedAccommodationIds}
                    accommodationTravelerMap={accommodationTravelerMap}
                    travelers={travelers}
                    invalidAccommodationIds={invalidAccommodationIds}
                    onAccommodationToggle={handleAccommodationToggle}
                    onAccommodationTravelersChange={handleAccommodationTravelersChange}
                    selectedAddonIds={selectedAddonIds}
                    invalidAddonIds={invalidAddonIds}
                    addonTravelerMap={addonTravelerMap}
                    onAddonToggle={handleAddonToggle}
                    nights={tripNights}
                    onAddonTravelersChange={handleAddonTravelersChange}
                    lockedAccommodationIds={lockedAccommodationIds}
                    lockedAddonIds={lockedAddonIds}
                    lockedAccommodationTravelerMap={lockedAccommodationTravelerMap}
                    lockedAddonTravelerMap={lockedAddonTravelerMap}
                    readOnly={isOrderCancelledOrFailed}
                />
            </>
        );
        case 3:
            return (
                <>
                    {isSupplementMode && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A payment link was already generated for this booking, so the payment
                            plan can't be changed. You can resend the link.
                        </Alert>
                    )}
                    <ReviewPaymentStep
                        selectedTripPackage={selectedTripPackage}
                        selectedDepartureDate={selectedDepartureDate}
                        paymentError={paymentError}
                        onEditStep={isOrderCancelledOrFailed ? null : handleEditFromReview}
                        flightPreference={flightPreference}
                        travelers={travelers}
                        selectedAccommodations={selectedAccommodations}
                        accommodationTravelerMap={accommodationTravelerMap}
                        selectedAddons={selectedAddons}
                        addonTravelerMap={addonTravelerMap}
                        isLoadingTemplates={isLoadingTemplates}
                        templatesError={templatesError}
                        paymentTemplates={paymentTemplates}
                        invalidPaymentTemplate={invalidPaymentTemplate}
                        selectedPaymentTemplateId={selectedPaymentTemplateId}
                        onSelectPaymentTemplate={handleSelectPaymentTemplate}
                        gatewayAvailability={gatewayAvailability}
                        isLoadingGateways={isLoadingGateways}
                        gatewayError={gatewayError}
                        selectedGateway={selectedGateway}
                        onGatewayChange={setSelectedGateway}
                        pricing={reviewPricing}
                        bookingStatus={bookingStatus}
                        isProcessingPayment={isProcessingPayment}
                        onPayNow={handlePayNow}
                        paymentTermPolicies={paymentTermPolicies}
                        isLoadingPolicies={isLoadingPolicies}
                        isSupplementMode={isSupplementMode}
                        supplement={supplement}
                        readOnly={isOrderCancelledOrFailed}
                    />
                </>
            );
      case 4:
        return (
          <MDBox py={4} textAlign="center">
            <MDTypography variant="h4" fontWeight="bold" color="success" mb={1}>
              Payment Link Sent
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={0.5}>
              The payment link has been sent to the customer. The booking will be confirmed once payment is received.
            </MDTypography>
              <MDTypography variant="button" fontWeight="medium" color="dark" display="block" mb={2}>
                  Order ID:{" "}
                  {orderNumericId ? (
                      <MDTypography
                          component={RouterLink}
                          to={`/travel-order/${orderNumericId}`}
                          variant="button"
                          fontWeight="bold"
                          color="info"
                          sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                          {latestOrderId || `#${orderNumericId}`}
                      </MDTypography>
                  ) : (
                      "-"
                  )}
              </MDTypography>
            <MDButton
              variant="outlined"
              color="info"
              onClick={handleResendPaymentLink}
              disabled={isProcessingPayment}
            >
              {isProcessingPayment ? "Preparing…" : "Resend payment link"}
            </MDButton>
          </MDBox>
        );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SendPaymentLinkModal
        open={paymentLinkModalOpen}
        onClose={() => setPaymentLinkModalOpen(false)}
        invoiceId={paymentLinkInvoiceId}
        defaultEmail={paymentLinkDefaultEmail}
        onSent={handlePaymentLinkSent}
        updateSnackbar={setSnackbar}
        isSupplement={Boolean(isSupplementMode && supplementInvoiceRef.current && paymentLinkInvoiceId === supplementInvoiceRef.current)}
      />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ overflow: "visible" }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                  <MDBox>
                    <MDTypography variant="h5" color="white">
                      {isEditMode ? "Edit Trip Booking" : "New Trip Booking"}   {/* CHANGED — was static "Create Trip Booking" */}
                    </MDTypography>
                    <MDTypography variant="caption" color="white" opacity={0.9} display="block" mt={0.5}>
                      Step {activeStep + 1} of {WIZARD_STEPS.length} — {WIZARD_STEPS[activeStep]}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <MDButton variant="outlined" color="white" size="small" onClick={handleCancelBooking}>
                      Cancel
                    </MDButton>
                  </MDBox>
                </MDBox>

                <OrderWizardStepper
                  steps={WIZARD_STEPS}
                  activeStep={activeStep}
                  completedSteps={completedSteps}
                  onStepClick={handleStepClick}
                  allStepsClickable={isEditMode && !isLoadingOrderForEdit && Boolean(selectedTripPackage)}
                />
              </MDBox>

              <MDBox p={3}>
                {isOrderCancelledOrFailed && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This travel order is {originalOrderStatus || editOrderData?.status || "Cancelled"} and cannot be edited. All details are read-only.
                  </Alert>
                )}
                {stepError && !isOrderCancelledOrFailed && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {stepError}
                  </Alert>
                )}
                {renderStepContent()}

                <MDBox pt={3} sx={{borderTop: "1px solid", borderColor: "divider"}} display="flex" justifyContent="space-between" mt={3}>
                  <MDButton sx={{visibility: activeStep > 0 ? 'visible' : 'hidden'}} variant="outlined" color="dark" onClick={handleBack} disabled={activeStep === 0}>
                    Back
                  </MDButton>
                  {activeStep < 3 ? (
                      <MDButton
                          variant="gradient"
                          color="info"
                          onClick={handleNext}
                          endIcon={<ArrowForwardIcon />}
                          disabled={isProcessingPayment || isSavingStep}
                      >
                        {isSavingStep ? "Saving..." : nextLabel}
                      </MDButton>
                  ) : activeStep === 4 ? (
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() =>
                          navigate(orderNumericId ? `/travel-order/${orderNumericId}` : "/travel-orders")
                      }
                    >
                        {orderNumericId ? "View Order" : "Return to Orders"}
                    </MDButton>
                  ) : (
                    <MDBox />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <OrderSummaryPanel
                selectedTripPackage={selectedTripPackage}
                travellers={travelerCount}
                selectedDepartureDate={selectedDepartureDate}
                accommodationTravelerMap={accommodationTravelerMap}
                addonTravelerMap={addonTravelerMap}
                tripNights={tripNights}
                pricing={reviewPricing}
                hssTemplate={selectedHssTemplate}
                taxes={taxes}
                selectedAccommodations={baselineAccommodations}   // was selectedAccommodations
                selectedAddons={baselineAddons}                    // was selectedAddons
                supplement={isSupplementMode ? supplement : null} // NEW
            />
          </Grid>
        </Grid>
      </MDBox>
      <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {isEditMode ? "Cancel order editing?" : "Cancel booking process?"}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to cancel? Any unsaved progress will be lost.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={handleCancelDialogClose}
          >
            Keep Editing
          </MDButton>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleConfirmCancelBooking}
          >
            Yes, Cancel
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default CreateTravelOrder;
